import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Bike,
  Camera,
  ChevronLeft,
  Euro,
  Loader2,
  MapPin,
  MessageSquare,
  Navigation,
  Phone,
  User,
} from 'lucide-react';
import {
  INTERVENTION_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  type InterventionStatus,
  type PaymentMethod,
} from '@hch/shared';
import { Button } from '~/components/ui/button';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Textarea,
} from '~/components/ui/primitives';
import { ErrorState, LoadingState, StatusBadge } from '~/components/shared/states';
import { ConfirmDialog } from '~/components/shared/confirm-dialog';
import { api, uploadToStorage } from '~/lib/api';
import { useAuth } from '~/lib/auth';
import {
  messageOf,
  useAddComment,
  useAttachMedia,
  useCancelIntervention,
  useChangeInterventionStatus,
  useIntervention,
  useRegisterPayment,
  useUpdateIntervention,
} from '~/lib/queries';
import {
  directionsUrl,
  formatDateTime,
  formatPrice,
  formatRelativeDay,
  formatSlotRange,
  fullName,
} from '~/lib/utils';

/**
 * Dossier d'intervention — ecran partage par le technicien et l'administrateur.
 *
 * Les actions proposees ne sont pas devinees cote client : l'API renvoie
 * `availableTransitions`, calcule par la machine a etats en fonction du statut ET
 * du role. Le bouton affiche correspond donc toujours a une action reellement
 * autorisee par le serveur.
 */
export function InterventionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const { data: intervention, isLoading, error, refetch } = useIntervention(id);

  const changeStatus = useChangeInterventionStatus();
  const cancel = useCancelIntervention();
  const update = useUpdateIntervention();
  const addComment = useAddComment();
  const registerPayment = useRegisterPayment();
  const attachMedia = useAttachMedia();

  const [techNotes, setTechNotes] = React.useState('');
  const [comment, setComment] = React.useState('');
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [paymentOpen, setPaymentOpen] = React.useState(false);
  const [paymentMethod, setPaymentMethod] = React.useState<PaymentMethod>('CARD');
  const [uploading, setUploading] = React.useState(false);

  React.useEffect(() => {
    if (intervention) setTechNotes(intervention.techNotes ?? '');
  }, [intervention?.id, intervention?.techNotes]);

  if (isLoading)
    return (
      <div className="app-page">
        <LoadingState rows={4} />
      </div>
    );
  if (error)
    return (
      <div className="app-page">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  if (!intervention) return null;

  const canEdit = hasRole('TECHNICIAN', 'ADMIN');
  const transitions = (intervention.availableTransitions ?? []).filter(
    (status) => status !== 'CANCELLED',
  ) as InterventionStatus[];

  async function handlePhotos(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0 || !id) return;

    setUploading(true);
    try {
      for (const file of files) {
        const presigned = await api.post<{ uploadUrl: string; storageKey: string }>(
          '/media/presign',
          {
            fileName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
            phase: 'AFTER',
          },
        );
        await uploadToStorage(presigned.uploadUrl, file);
        await attachMedia.mutateAsync({
          id,
          input: { storageKey: presigned.storageKey, phase: 'AFTER' },
        });
      }
    } catch (uploadError) {
      toast.error(messageOf(uploadError));
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  const beforePhotos = intervention.media?.filter((file) => file.phase === 'BEFORE') ?? [];
  const afterPhotos = intervention.media?.filter((file) => file.phase === 'AFTER') ?? [];

  return (
    <div className="app-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ChevronLeft aria-hidden />
          Retour
        </Button>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="app-page-title">{intervention.packageName}</h1>
            <StatusBadge status={intervention.status} />
          </div>
          <p className="text-sm text-muted-foreground">
            Reference {intervention.reference} ·{' '}
            <span className="capitalize">{formatRelativeDay(intervention.startsAt)}</span>{' '}
            {formatSlotRange(intervention.startsAt, intervention.endsAt)}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {transitions.map((status) => (
            <Button
              key={status}
              size="touch"
              variant={status === 'DONE' ? 'success' : 'default'}
              loading={changeStatus.isPending}
              onClick={() =>
                void changeStatus.mutateAsync({ id: intervention.id, input: { status } })
              }
            >
              {actionLabel(status)}
            </Button>
          ))}

          {intervention.status === 'DONE' && !intervention.payment ? (
            <Button size="touch" onClick={() => setPaymentOpen(true)}>
              <Euro aria-hidden />
              Encaisser
            </Button>
          ) : null}

          {['BOOKED', 'CONFIRMED', 'IN_PROGRESS'].includes(intervention.status) ? (
            <Button size="touch" variant="outline" onClick={() => setCancelOpen(true)}>
              Annuler
            </Button>
          ) : null}
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* --- Client et lieu --------------------------------------------------- */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <User className="size-4" aria-hidden />
              Client
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <p className="font-medium">{fullName(intervention.client)}</p>
              {intervention.client?.isGuest ? (
                <Badge variant="outline" className="mt-1">
                  Compte non finalise
                </Badge>
              ) : null}
            </div>

            {intervention.client?.phone ? (
              <a
                href={`tel:${intervention.client.phone}`}
                className="flex items-center gap-2 text-foreground hover:underline"
              >
                <Phone className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                {intervention.client.phone}
              </a>
            ) : null}

            <Separator />

            {intervention.address ? (
              <div className="space-y-2">
                <p className="flex items-start gap-2">
                  <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span>
                    {intervention.address.street}
                    <br />
                    {intervention.address.postalCode} {intervention.address.city}
                  </span>
                </p>
                <Button asChild variant="outline" size="sm" className="w-full">
                  <a
                    href={directionsUrl(intervention.address.lat, intervention.address.lng)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Navigation aria-hidden />
                    Itineraire
                  </a>
                </Button>
              </div>
            ) : null}

            <Separator />

            {intervention.bicycle ? (
              <p className="flex items-start gap-2">
                <Bike className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span>
                  {intervention.bicycle.brand} {intervention.bicycle.model}
                  <br />
                  <span className="text-muted-foreground">
                    {intervention.bicycle.manufacturedYear} ·{' '}
                    {intervention.bicycle.type === 'EBIKE' ? 'VAE' : 'Velo musculaire'}
                  </span>
                </span>
              </p>
            ) : null}
          </CardContent>
        </Card>

        {/* --- Prestation ------------------------------------------------------- */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm">Prestation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="app-summary-row">
              <span>{intervention.packageName}</span>
              <span className="tabular-nums">
                {formatPrice(intervention.packagePriceSnapshot ?? 0)}
              </span>
            </div>

            {intervention.products?.map((line) => (
              <div key={line.id} className="app-summary-row text-muted-foreground">
                <span>
                  {line.name} × {line.quantity}
                </span>
                <span className="tabular-nums">
                  {formatPrice(line.unitPriceSnapshot * line.quantity)}
                </span>
              </div>
            ))}

            <Separator />

            <div className="app-summary-row text-base font-semibold">
              <span>Total</span>
              <span className="tabular-nums">{formatPrice(intervention.totalAmount)}</span>
            </div>

            {intervention.payment ? (
              <p className="text-xs text-success">
                Encaisse le {formatDateTime(intervention.payment.paidAt ?? '')} —{' '}
                {PAYMENT_METHOD_LABELS[intervention.payment.method]}
              </p>
            ) : null}

            {intervention.clientNotes ? (
              <>
                <Separator />
                <div className="app-summary-box">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Precisions du client
                  </p>
                  <p className="whitespace-pre-line">{intervention.clientNotes}</p>
                </div>
              </>
            ) : null}

            {intervention.cancellationReason ? (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-destructive">
                  Motif d'annulation
                </p>
                <p>{intervention.cancellationReason}</p>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* --- Compte rendu ------------------------------------------------------- */}
      {canEdit ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Compte rendu</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Notes techniques" htmlFor="techNotes">
              <Textarea
                value={techNotes}
                onChange={(event) => setTechNotes(event.target.value)}
                rows={4}
                placeholder="Travaux realises, pieces remplacees, recommandations…"
                maxLength={5000}
              />
            </Field>
            <Button
              size="sm"
              loading={update.isPending}
              onClick={() => void update.mutateAsync({ id: intervention.id, input: { techNotes } })}
            >
              Enregistrer le compte rendu
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {/* --- Photos -------------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Camera className="size-4" aria-hidden />
            Photos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <PhotoGrid title="Avant intervention" photos={beforePhotos} />
          <PhotoGrid title="Apres intervention" photos={afterPhotos} />

          {canEdit && intervention.status !== 'CANCELLED' ? (
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-border p-4 transition-colors hover:bg-accent">
              <Camera className="size-5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="flex-1 text-sm">
                {uploading ? 'Televersement en cours…' : 'Ajouter une photo du travail realise'}
              </span>
              {uploading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                multiple
                className="sr-only"
                onChange={(event) => void handlePhotos(event)}
                disabled={uploading}
              />
            </label>
          ) : null}
        </CardContent>
      </Card>

      {/* --- Commentaires --------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <MessageSquare className="size-4" aria-hidden />
            Suivi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {intervention.comments?.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun commentaire pour le moment.</p>
          ) : null}

          <ul className="flex flex-col gap-3">
            {intervention.comments?.map((entry) => (
              <li key={entry.id} className="app-summary-box space-y-1">
                <p className="text-xs text-muted-foreground">
                  {entry.authorName} · {formatDateTime(entry.createdAt)}
                </p>
                <p className="text-sm whitespace-pre-line">{entry.content}</p>
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Ajouter un commentaire…"
              aria-label="Nouveau commentaire"
            />
            <Button
              disabled={comment.trim().length === 0}
              loading={addComment.isPending}
              onClick={async () => {
                await addComment.mutateAsync({
                  id: intervention.id,
                  input: { content: comment.trim() },
                });
                setComment('');
              }}
            >
              Publier
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* --- Dialogues -------------------------------------------------------------- */}
      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Annuler cette intervention ?"
        description="Les creneaux reserves seront immediatement liberes."
        variant="destructive"
        confirmLabel="Annuler l'intervention"
        cancelLabel="Conserver"
        requireReason
        reasonLabel="Motif"
        loading={cancel.isPending}
        onConfirm={async (reason) => {
          await cancel.mutateAsync({ id: intervention.id, input: { reason: reason ?? '' } });
          setCancelOpen(false);
        }}
      />

      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Encaissement</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="app-summary-box app-summary-row">
              <span className="text-sm text-muted-foreground">Montant du</span>
              <span className="text-lg font-semibold tabular-nums">
                {formatPrice(intervention.totalAmount)}
              </span>
            </div>

            <Field label="Moyen de paiement" htmlFor="method" required>
              <Select
                value={paymentMethod}
                onValueChange={(value) => setPaymentMethod(value as PaymentMethod)}
              >
                <SelectTrigger id="method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {PAYMENT_METHOD_LABELS[method]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentOpen(false)}>
              Annuler
            </Button>
            <Button
              loading={registerPayment.isPending}
              onClick={async () => {
                await registerPayment.mutateAsync({
                  id: intervention.id,
                  input: { method: paymentMethod, amount: intervention.totalAmount },
                });
                setPaymentOpen(false);
              }}
            >
              Valider l'encaissement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoGrid({
  title,
  photos,
}: {
  title: string;
  photos: Array<{ id: string; url: string; createdAt: string }>;
}) {
  if (photos.length === 0) return null;

  return (
    <section className="space-y-2">
      <h3 className="app-section-title">{title}</h3>
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {photos.map((photo) => (
          <li key={photo.id}>
            <a href={photo.url} target="_blank" rel="noreferrer" className="block">
              <img
                src={photo.url}
                alt={`${title} — cliche du ${formatDateTime(photo.createdAt)}`}
                className="aspect-square w-full rounded-lg border border-border object-cover transition-opacity hover:opacity-90"
                loading="lazy"
              />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Libelle d'action, plus parlant que le nom brut de l'etat cible. */
function actionLabel(status: InterventionStatus): string {
  const labels: Partial<Record<InterventionStatus, string>> = {
    CONFIRMED: 'Confirmer',
    IN_PROGRESS: "Demarrer l'intervention",
    DONE: 'Marquer comme terminee',
    PAID: 'Marquer comme payee',
    NO_SHOW: 'Client absent',
  };
  return labels[status] ?? INTERVENTION_STATUS_LABELS[status];
}
